<script lang="ts" setup>
import type {
  AssistantCreatedSession,
  AssistantPendingQuestion,
  AssistantQuestion,
  AssistantSessionNavigationOptions,
  AssistantSessionSummary,
  AssistantSessionUpdate,
} from './types';
import {
  useDraggable,
  useElementSize,
  useScrollLock,
  useTimeoutFn,
  useWindowSize,
} from '@vueuse/core';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import AssistantChatHeader from './AssistantChatHeader.vue';
import AssistantConversation from './AssistantConversation.vue';
import AssistantSessionHistory from './AssistantSessionHistory.vue';

interface AssistantStoredConfig {
  greeting?: string;
  recommendedQuestions?: string[];
  title?: string;
  updatedAt?: number;
}

interface PanelPoint {
  x: number;
  y: number;
}

interface PanelSize {
  height: number;
  width: number;
}

type ResizeMode = 'bottomRight' | 'topLeft';

interface ResizeStartState {
  height: number;
  mode: ResizeMode;
  positionX: number;
  positionY: number;
  pointerX: number;
  pointerY: number;
  width: number;
}

const props = defineProps<{
  pendingQuestion: AssistantPendingQuestion | null;
  questions: AssistantQuestion[];
}>();

const emit = defineEmits<{
  pendingQuestionHandled: [questionId: number];
  questionsUpdated: [questions: string[]];
}>();

const DEFAULT_TITLE = 'OpenAtom AI 助手';
const CONFIG_STORAGE_KEY = 'openatom_assistant_config';
const DRAG_DESKTOP_BREAKPOINT = 768;
const MIN_VISIBLE_PANEL_WIDTH = 120;
const MIN_VISIBLE_HEADER_HEIGHT = 56;
const MIN_RESIZABLE_PANEL_WIDTH = 420;
const MIN_RESIZABLE_PANEL_HEIGHT = 420;
const RESIZE_HANDLE_VISIBLE_OFFSET = 16;
const ERROR_MESSAGE_AUTO_DISMISS_DELAY = 6000;

const isOpen = defineModel<boolean>('open', { default: false });

const hasOpened = ref(false);
const activeConversationId = ref('');
const isHistoryVisible = ref(false);
const isMaximized = ref(false);
const hasCustomPanelPosition = ref(false);
const hasCustomPanelSize = ref(false);
const panelPosition = ref<PanelPoint>({ x: 0, y: 0 });
const panelSize = ref<PanelSize>({ height: 0, width: 0 });
const resizeStartState = ref<ResizeStartState | null>(null);
const title = ref(DEFAULT_TITLE);
const greeting = ref('');
const errorMessage = ref('');
const sessions = ref<AssistantSessionSummary[]>([]);
const sessionUpdate = ref<AssistantSessionUpdate | null>(null);

const openButtonRef = useTemplateRef('openButton');
const chatPanelRef = useTemplateRef('chatPanel');
const dragHandleRef = useTemplateRef('dragHandle');
const bottomRightResizeHandleRef = useTemplateRef('bottomRightResizeHandle');
const topLeftResizeHandleRef = useTemplateRef('topLeftResizeHandle');
const sessionHistoryRef = useTemplateRef('sessionHistory');
const conversationRef = useTemplateRef('conversation');

const isPageScrollLocked = useScrollLock(typeof document === 'undefined' ? null : document.documentElement);
const { width: windowWidth, height: windowHeight } = useWindowSize({ initialWidth: 0, initialHeight: 0 });
const { width: panelWidth } = useElementSize(chatPanelRef);
const { height: dragHandleHeight } = useElementSize(dragHandleRef);

const isCompactViewport = computed(() => windowWidth.value > 0 && windowWidth.value < DRAG_DESKTOP_BREAKPOINT);
const isPanelDraggable = computed(() => isOpen.value && !isMaximized.value && !isCompactViewport.value);
const isPanelResizable = computed(() => isPanelDraggable.value);
const shouldUseCustomPanelPosition = computed(() => hasCustomPanelPosition.value && isPanelDraggable.value);
const shouldUseCustomPanelSize = computed(() => hasCustomPanelSize.value && isPanelResizable.value);
const activeSessionTitle = computed(() => {
  const activeSession = sessions.value.find(session => session.conversationId === activeConversationId.value);

  return activeSession?.title ?? '新的咨询';
});
const { start: startErrorMessageAutoDismiss, stop: stopErrorMessageAutoDismiss } = useTimeoutFn(() => {
  errorMessage.value = '';
}, ERROR_MESSAGE_AUTO_DISMISS_DELAY, { immediate: false });

const clampValue = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getViewportSize = () => ({
  height: windowHeight.value || (typeof window === 'undefined' ? 0 : window.innerHeight),
  width: windowWidth.value || (typeof window === 'undefined' ? 0 : window.innerWidth),
});

const getPanelWidth = () => chatPanelRef.value?.getBoundingClientRect().width || panelWidth.value || 0;

const getDragHandleHeight = () => dragHandleRef.value?.getBoundingClientRect().height || dragHandleHeight.value || MIN_VISIBLE_HEADER_HEIGHT;

const getPanelSize = (): PanelSize => {
  const panelRect = chatPanelRef.value?.getBoundingClientRect();

  return {
    height: panelRect?.height || panelSize.value.height || 0,
    width: panelRect?.width || panelSize.value.width || panelWidth.value || 0,
  };
};

const clampPanelPosition = (position: PanelPoint, size = getPanelSize()) => {
  const { height: viewportHeight, width: viewportWidth } = getViewportSize();
  const currentPanelWidth = size.width || getPanelWidth();
  const currentDragHandleHeight = getDragHandleHeight();

  if (!viewportHeight || !viewportWidth || !currentPanelWidth || !currentDragHandleHeight) {
    return position;
  }

  const visiblePanelWidth = Math.min(MIN_VISIBLE_PANEL_WIDTH, currentPanelWidth, viewportWidth);
  const visibleHeaderHeight = Math.min(MIN_VISIBLE_HEADER_HEIGHT, currentDragHandleHeight, viewportHeight);

  return {
    x: clampValue(position.x, visiblePanelWidth - currentPanelWidth, viewportWidth - visiblePanelWidth),
    y: clampValue(position.y, visibleHeaderHeight - currentDragHandleHeight, viewportHeight - visibleHeaderHeight),
  };
};

const clampPanelSize = (size: PanelSize, position = panelPosition.value) => {
  const { height: viewportHeight, width: viewportWidth } = getViewportSize();

  if (!viewportHeight || !viewportWidth) {
    return size;
  }

  const maxWidth = Math.max(MIN_RESIZABLE_PANEL_WIDTH, viewportWidth - position.x - RESIZE_HANDLE_VISIBLE_OFFSET);
  const maxHeight = Math.max(MIN_RESIZABLE_PANEL_HEIGHT, viewportHeight - position.y - RESIZE_HANDLE_VISIBLE_OFFSET);

  return {
    height: clampValue(size.height, MIN_RESIZABLE_PANEL_HEIGHT, maxHeight),
    width: clampValue(size.width, MIN_RESIZABLE_PANEL_WIDTH, maxWidth),
  };
};

const clampTopLeftPanelSize = (size: PanelSize, fixedEdges: { bottom: number; right: number }) => {
  const { height: viewportHeight, width: viewportWidth } = getViewportSize();

  if (!viewportHeight || !viewportWidth) {
    return size;
  }

  const visibleRight = Math.min(fixedEdges.right, viewportWidth - RESIZE_HANDLE_VISIBLE_OFFSET);
  const visibleBottom = Math.min(fixedEdges.bottom, viewportHeight - RESIZE_HANDLE_VISIBLE_OFFSET);
  const maxWidth = Math.max(MIN_RESIZABLE_PANEL_WIDTH, visibleRight - RESIZE_HANDLE_VISIBLE_OFFSET);
  const maxHeight = Math.max(MIN_RESIZABLE_PANEL_HEIGHT, visibleBottom - RESIZE_HANDLE_VISIBLE_OFFSET);

  return {
    height: clampValue(size.height, MIN_RESIZABLE_PANEL_HEIGHT, maxHeight),
    width: clampValue(size.width, MIN_RESIZABLE_PANEL_WIDTH, maxWidth),
  };
};

const clampCustomPanelPosition = () => {
  if (!hasCustomPanelPosition.value) {
    return;
  }

  panelPosition.value = clampPanelPosition(panelPosition.value);
};

const clampCustomPanelGeometry = () => {
  if (hasCustomPanelSize.value) {
    panelSize.value = clampPanelSize(panelSize.value);
  }

  clampCustomPanelPosition();
};

const shouldCancelPanelDrag = (event: PointerEvent) => {
  const target = event.target;

  return target instanceof Element && Boolean(target.closest('a, button, input, select, textarea, [data-assistant-drag-cancel="true"]'));
};

const prepareCustomPanelGeometry = () => {
  const panelRect = chatPanelRef.value?.getBoundingClientRect();

  if (!panelRect) {
    return null;
  }

  hasCustomPanelPosition.value = true;
  panelPosition.value = clampPanelPosition({
    x: panelRect.left,
    y: panelRect.top,
  }, {
    height: panelRect.height,
    width: panelRect.width,
  });

  return panelRect;
};

const startPanelResize = (mode: ResizeMode, event: PointerEvent) => {
  const panelRect = prepareCustomPanelGeometry();

  if (!panelRect) {
    return false;
  }

  hasCustomPanelSize.value = true;
  const nextSize = clampPanelSize({
    height: panelRect.height,
    width: panelRect.width,
  });

  panelSize.value = nextSize;
  resizeStartState.value = {
    height: nextSize.height,
    mode,
    pointerX: event.clientX,
    pointerY: event.clientY,
    positionX: panelPosition.value.x,
    positionY: panelPosition.value.y,
    width: nextSize.width,
  };
};

const getResizedPanelGeometry = (event: PointerEvent) => {
  const resizeStart = resizeStartState.value;

  if (!resizeStart) {
    return null;
  }

  const deltaX = event.clientX - resizeStart.pointerX;
  const deltaY = event.clientY - resizeStart.pointerY;

  if (resizeStart.mode === 'topLeft') {
    const fixedEdges = {
      bottom: resizeStart.positionY + resizeStart.height,
      right: resizeStart.positionX + resizeStart.width,
    };
    const nextSize = clampTopLeftPanelSize({
      height: resizeStart.height - deltaY,
      width: resizeStart.width - deltaX,
    }, fixedEdges);
    const nextPosition = clampPanelPosition({
      x: fixedEdges.right - nextSize.width,
      y: fixedEdges.bottom - nextSize.height,
    }, nextSize);

    return {
      position: nextPosition,
      size: nextSize,
    };
  }

  const nextSize = clampPanelSize({
    height: resizeStart.height + deltaY,
    width: resizeStart.width + deltaX,
  }, {
    x: resizeStart.positionX,
    y: resizeStart.positionY,
  });
  const nextPosition = clampPanelPosition({
    x: resizeStart.positionX,
    y: resizeStart.positionY,
  }, nextSize);

  return {
    position: nextPosition,
    size: nextSize,
  };
};

const applyPanelResize = (event: PointerEvent) => {
  const nextGeometry = getResizedPanelGeometry(event);

  if (!nextGeometry) {
    return false;
  }

  panelSize.value = nextGeometry.size;
  panelPosition.value = nextGeometry.position;
};

const { isDragging: isPanelDragging } = useDraggable(chatPanelRef, {
  buttons: [0],
  disabled: computed(() => !isPanelDraggable.value),
  handle: dragHandleRef,
  preventDefault: true,
  onStart: (_position, event) => {
    if (shouldCancelPanelDrag(event)) {
      return false;
    }

    if (!prepareCustomPanelGeometry()) {
      return false;
    }
  },
  onMove: (position) => {
    panelPosition.value = clampPanelPosition(position);
  },
  onEnd: (position) => {
    panelPosition.value = clampPanelPosition(position);
  },
});
const { isDragging: isBottomRightPanelResizing } = useDraggable(bottomRightResizeHandleRef, {
  buttons: [0],
  disabled: computed(() => !isPanelResizable.value),
  preventDefault: true,
  onStart: (_position, event) => {
    return startPanelResize('bottomRight', event);
  },
  onMove: (_position, event) => {
    applyPanelResize(event);
  },
  onEnd: (_position, event) => {
    const hasResized = applyPanelResize(event) !== false;
    resizeStartState.value = null;

    if (hasResized && !isHistoryVisible.value) {
      void scrollConversationToBottom();
    }
  },
});
const { isDragging: isTopLeftPanelResizing } = useDraggable(topLeftResizeHandleRef, {
  buttons: [0],
  disabled: computed(() => !isPanelResizable.value),
  preventDefault: true,
  onStart: (_position, event) => {
    return startPanelResize('topLeft', event);
  },
  onMove: (_position, event) => {
    applyPanelResize(event);
  },
  onEnd: (_position, event) => {
    const hasResized = applyPanelResize(event) !== false;
    resizeStartState.value = null;

    if (hasResized && !isHistoryVisible.value) {
      void scrollConversationToBottom();
    }
  },
});
const isPanelResizing = computed(() => isBottomRightPanelResizing.value || isTopLeftPanelResizing.value);

const openChatPanel = () => {
  hasOpened.value = true;
  isOpen.value = true;
};

const readStoredConfig = (): AssistantStoredConfig | null => {
  const rawConfig = window.localStorage.getItem(CONFIG_STORAGE_KEY);

  if (!rawConfig) {
    return null;
  }

  try {
    const parsedConfig = JSON.parse(rawConfig);

    if (!parsedConfig || typeof parsedConfig !== 'object') {
      return null;
    }

    return parsedConfig;
  }
  catch {
    return null;
  }
};

const applyAssistantConfig = (config: AssistantStoredConfig) => {
  title.value = config.title || DEFAULT_TITLE;
  greeting.value = config.greeting || '';

  emit('questionsUpdated', config.recommendedQuestions ?? []);
};

const saveAssistantConfig = (config: AssistantStoredConfig) => {
  window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(config));
};

const focusConversation = async () => {
  await nextTick();
  await conversationRef.value?.focusInput();
};

const scrollConversationToBottom = async () => {
  await nextTick();
  conversationRef.value?.scrollMessagesToBottom();
};

const setErrorMessage = (message: string) => {
  stopErrorMessageAutoDismiss();
  errorMessage.value = message;

  if (message) {
    startErrorMessageAutoDismiss();
  }
};

const clearErrorMessage = () => {
  stopErrorMessageAutoDismiss();
  errorMessage.value = '';
};

const resetPanelPosition = () => {
  hasCustomPanelPosition.value = false;
  hasCustomPanelSize.value = false;
  panelPosition.value = { x: 0, y: 0 };
  panelSize.value = { height: 0, width: 0 };
  resizeStartState.value = null;
};

const closeChatPanel = async () => {
  isOpen.value = false;
  isMaximized.value = false;

  resetPanelPosition();
  clearErrorMessage();

  await nextTick();

  openButtonRef.value?.focus();
};

const togglePanelSize = async () => {
  isMaximized.value = !isMaximized.value;

  await nextTick();

  if (!isMaximized.value) {
    clampCustomPanelGeometry();
  }

  if (!isHistoryVisible.value) {
    await scrollConversationToBottom();
  }
};

const showChatView = () => {
  isHistoryVisible.value = false;
};

const showHistoryView = () => {
  isHistoryVisible.value = true;
};

const startNewChat = async () => {
  clearErrorMessage();
  await sessionHistoryRef.value?.createSession();
  showChatView();
};

const shouldShowChat = (options?: AssistantSessionNavigationOptions) => options?.showChat !== false;

const selectSession = (conversationId: string, options?: AssistantSessionNavigationOptions) => {
  activeConversationId.value = conversationId;
  clearErrorMessage();

  if (shouldShowChat(options)) {
    showChatView();
  }
};

const handleSessionCreated = (session: AssistantCreatedSession, options?: AssistantSessionNavigationOptions) => {
  activeConversationId.value = session.conversationId;

  const nextConfig: AssistantStoredConfig = {
    greeting: session.greeting ?? '',
    recommendedQuestions: session.recommendedQuestions ?? [],
    title: session.title || DEFAULT_TITLE,
    updatedAt: Date.now(),
  };

  applyAssistantConfig(nextConfig);
  saveAssistantConfig(nextConfig);

  if (shouldShowChat(options)) {
    showChatView();
  }
};

const handleSessionsChange = (nextSessions: AssistantSessionSummary[]) => {
  sessions.value = nextSessions;
};

const handleSessionUpdate = (update: AssistantSessionUpdate) => {
  sessionUpdate.value = { ...update };
  sessions.value = sessions.value.map((session) => {
    if (session.conversationId !== update.conversationId) {
      return session;
    }

    return {
      ...session,
      title: update.title ?? session.title,
      preview: update.preview ?? session.preview,
      updatedAt: update.updatedAt ?? session.updatedAt,
    };
  });
};

watch(isOpen, async (open) => {
  if (!open) {
    isMaximized.value = false;

    resetPanelPosition();
    clearErrorMessage();

    return;
  }

  hasOpened.value = true;

  if (isHistoryVisible.value) {
    return;
  }

  await focusConversation();
  await scrollConversationToBottom();
});

watch(isMaximized, (maximized) => {
  isPageScrollLocked.value = maximized;
});

watch([windowWidth, windowHeight, panelWidth, dragHandleHeight], () => {
  if (!shouldUseCustomPanelPosition.value && !shouldUseCustomPanelSize.value) {
    return;
  }

  clampCustomPanelGeometry();
});

watch(isHistoryVisible, async (visible) => {
  if (visible || !isOpen.value) {
    return;
  }

  await focusConversation();
  await scrollConversationToBottom();
});

watch(() => props.pendingQuestion, (pendingQuestion) => {
  if (pendingQuestion) {
    showChatView();
  }
});

onMounted(() => {
  const storedConfig = readStoredConfig();

  if (storedConfig) {
    applyAssistantConfig(storedConfig);
  }
});

onBeforeUnmount(() => {
  stopErrorMessageAutoDismiss();
  isPageScrollLocked.value = false;
});
</script>

<template>
  <div
    class="assistant-float box-border [&_*]:box-border"
    aria-live="polite"
  >
    <button
      v-show="!isOpen"
      ref="openButton"
      type="button"
      class="[transition:transform_0.25s_ease,box-shadow_0.25s_ease] fixed bottom-32 right-32 z-1000 h-56 flex cursor-pointer items-center gap-10 border-0 rounded-full bg-[linear-gradient(135deg,#1281ff_0%,#0b63d8_100%)] py-0 pl-10 pr-20 text-white shadow-[0_18px_38px_rgba(18,129,255,0.28)] motion-reduce:[transition:none] <md:bottom-18 <md:left-16 <md:right-16 <md:justify-center hover:translate-y--3 hover:shadow-[0_22px_44px_rgba(18,129,255,0.34)] motion-reduce:hover:[transform:none]"
      aria-label="打开 OpenAtom AI 助手"
      :aria-expanded="isOpen"
      @click="openChatPanel"
    >
      <span class="h-40 w-40 flex-center rounded-full bg-white text-14 text-#1281FF font-800 tracking-[0.02em]" aria-hidden="true">AI</span>
      <span class="text-16 font-700 leading-24">AI 助手</span>
    </button>

    <div
      v-if="hasOpened"
      v-show="isOpen"
      ref="chatPanel"
      class="fixed flex flex-col overflow-hidden border-1 border-#DBE8F7 rounded-22 border-solid bg-white shadow-[0_24px_70px_rgba(37,50,75,0.2)]"
      :class="[
        isMaximized ? 'inset-0 z-1000 h-screen max-h-none w-screen max-w-none border-0 rounded-0 shadow-none' : 'bottom-32 right-32 z-1000 <md:bottom-18 <md:left-16 <md:right-16',
        !shouldUseCustomPanelSize && 'max-h-[calc(100vh-64px)] w-720 max-w-[calc(100vw-64px)] <md:max-h-[calc(100vh-36px)] <md:max-w-none <md:w-auto <md:rounded-18',
      ]"
      :style="{
        ...(shouldUseCustomPanelPosition && {
          top: `${panelPosition.y}px`,
          left: `${panelPosition.x}px`,
          right: 'auto',
          bottom: 'auto',
        }),
        ...(shouldUseCustomPanelSize && {
          width: `${panelSize.width}px`,
          height: `${panelSize.height}px`,
        }),
      }"
      role="dialog"
      aria-label="OpenAtom AI 助手聊天窗口"
    >
      <div
        ref="dragHandle"
        :class="{
          'cursor-grabbing select-none touch-none': isPanelDragging && isPanelDraggable,
          'cursor-grab touch-none': !isPanelDragging && isPanelDraggable,
        }"
      >
        <AssistantChatHeader
          :is-history-visible="isHistoryVisible"
          :is-maximized="isMaximized"
          :subtitle="activeSessionTitle"
          :title="title"
          @close="closeChatPanel"
          @new-chat="startNewChat"
          @show-chat="showChatView"
          @show-history="showHistoryView"
          @toggle-size="togglePanelSize"
        />
      </div>

      <div
        v-if="errorMessage"
        class="flex items-start gap-10 border-b-1 border-b-#FFE1E1 border-b-solid bg-#FFF5F5 px-18 py-10 text-13 text-#B42318 leading-20"
        role="alert"
      >
        <span class="min-w-0 flex-1">{{ errorMessage }}</span>
        <button
          type="button"
          class="size-22 flex flex-shrink-0 cursor-pointer items-center justify-center border-0 rounded-full bg-transparent p-0 text-#B42318 opacity-70 hover:bg-#FFE1E1 hover:opacity-100"
          aria-label="关闭错误提示"
          title="关闭错误提示"
          @click="clearErrorMessage"
        >
          <span class="i-ion:close block size-16"></span>
        </button>
      </div>

      <div
        class="relative min-h-0 overflow-hidden bg-[linear-gradient(180deg,#f8fbff_0%,#fff_100%)]"
        :class="[
          isMaximized || shouldUseCustomPanelSize ? 'h-auto min-h-0 flex-1' : 'h-558 max-h-[calc(100vh-160px)] <md:h-410 <md:max-h-[calc(100vh-128px)]'
        ]"
      >
        <AssistantConversation
          v-show="!isHistoryVisible"
          ref="conversation"
          :active-conversation-id="activeConversationId"
          :greeting="greeting"
          :pending-question="props.pendingQuestion"
          :questions="props.questions"
          @error="setErrorMessage"
          @pending-question-handled="emit('pendingQuestionHandled', $event)"
          @session-updated="handleSessionUpdate"
        />

        <AssistantSessionHistory
          v-show="isHistoryVisible"
          ref="sessionHistory"
          :active-conversation-id="activeConversationId"
          :session-update="sessionUpdate"
          @back="showChatView"
          @error="setErrorMessage"
          @select-session="selectSession"
          @session-created="handleSessionCreated"
          @sessions-change="handleSessionsChange"
        />
      </div>

      <span
        v-show="isPanelResizable"
        ref="topLeftResizeHandle"
        class="[clip-path:polygon(0_0,100%_0,0_100%)] absolute left-0 top-0 z-2 h-30 w-30 cursor-nwse-resize touch-none before:[background-image:repeating-linear-gradient(135deg,transparent_0,transparent_4px,currentcolor_4px,currentcolor_5px,transparent_5px,transparent_8px)] before:absolute before:left-6 before:top-6 before:h-16 before:w-16 before:content-empty hover:text-white/88"
        :class="isPanelResizing ? 'text-white/88' : 'text-white/62'"
        aria-hidden="true"
      ></span>

      <span
        v-show="isPanelResizable"
        ref="bottomRightResizeHandle"
        class="[clip-path:polygon(100%_0,100%_100%,0_100%)] absolute bottom-0 right-0 z-2 h-30 w-30 cursor-nwse-resize touch-none before:[background-image:repeating-linear-gradient(135deg,transparent_0,transparent_4px,currentcolor_4px,currentcolor_5px,transparent_5px,transparent_8px)] before:absolute before:bottom-6 before:right-6 before:h-16 before:w-16 before:content-empty hover:text-#1281FF/74"
        :class="isPanelResizing ? 'text-#1281FF/74' : 'text-#708195/58'"
        aria-hidden="true"
      ></span>
    </div>
  </div>
</template>
